import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Rnd } from "react-rnd";
import "./tabPanelStyle.css";
import {
  deleteShapesAPI,
  updateCanvasElementAPI,
} from "../../addSeat/floorManagementApi";
import { setSelectedElementDetails } from "../../addSeat/addSeatSlice";
import { shapeTypes, structures, decorTypes, tableTextures } from "../../utils/floorManagementEnum";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import LineSegmentTool from "../icons/LineSegmentTool";
import SplitFloorTool from "../icons/SplitFloorTool";
import StarTool from "../icons/StarTool";
import Chair from "../icons/Chair";
import PlantIcon from "../icons/PlantIcon";
import TreeIcon from "../icons/TreeIcon";
import FlowerIcon from "../icons/FlowerIcon";
import FountainIcon from "../icons/FountainIcon";
import StatueIcon from "../icons/StatueIcon";
import LampIcon from "../icons/LampIcon";

// Table texture patterns
const textureStyles = {
  wood: "linear-gradient(135deg, #8B4513 0%, #A0522D 25%, #8B4513 50%, #A0522D 75%, #8B4513 100%)",
  marble: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 25%, #f5f5f5 50%, #d0d0d0 75%, #f5f5f5 100%)",
  glass: "linear-gradient(135deg, rgba(200,230,255,0.8) 0%, rgba(255,255,255,0.9) 50%, rgba(200,230,255,0.8) 100%)",
  metal: "linear-gradient(135deg, #71797E 0%, #A9A9A9 25%, #71797E 50%, #A9A9A9 75%, #71797E 100%)",
  granite: "linear-gradient(135deg, #2F4F4F 0%, #696969 25%, #2F4F4F 50%, #808080 75%, #2F4F4F 100%)",
};

const TabPanel = () => {
  const { selectedFloorDetails, branch, selectedElementDetails } = useSelector(
    (store) => store.addSeatSlice
  );
  const dispatch = useDispatch();
  const [shapes, setShapes] = useState(selectedFloorDetails?.floorCanvas || []);
  const [debouncedUpdate, setDebouncedUpdate] = useState(null);
  const [rotatingElementId, setRotatingElementId] = useState(null);
  const [pendingRotations, setPendingRotations] = useState({});
  const isRotatingRef = useRef(false);
  const rotationStartRef = useRef({ angle: 0, startAngle: 0 });
  const shapesRef = useRef(shapes);

  // Keep shapesRef in sync
  useEffect(() => {
    shapesRef.current = shapes;
  }, [shapes]);

  const containerRef = useRef();
  //handle selected
  const handleShapeSelected = (shape) => {
    dispatch(setSelectedElementDetails(shape));
  };

  useEffect(() => {
    setShapes(selectedFloorDetails?.floorCanvas || []);
  }, [selectedFloorDetails]);

  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;

      setShapes((prevShapes) =>
        prevShapes.map((s) => ({
          ...s,
          position: {
            x: (s.position.x / s.containerWidth) * containerWidth,
            y: (s.position.y / s.containerWidth) * containerWidth,
          },
          containerWidth: containerWidth,
        }))
      );
    }
  }, [containerRef.current?.clientWidth]);

  const handleSizeChange = (id, size, position, shape) => {
    const containerWidth = containerRef.current?.clientWidth || 0;
    const containerHeight = (containerWidth * 3) / 4;

    let maxPositionX = containerWidth - size.width;
    let maxPositionY = containerHeight - size.height;

    if (shape.type === shapeTypes.CHAIR) {
      size.width = 25;
      size.height = 25;
    }

    const boundedPosition = {
      x: Math.min(Math.max(position.x, 0), maxPositionX),
      y: Math.min(Math.max(position.y, 0), maxPositionY),
    };

    setShapes((prevShapes) =>
      prevShapes.map((s) =>
        s._id === id ? { ...s, size, position: boundedPosition } : s
      )
    );

    clearTimeout(debouncedUpdate);
    const timeout = setTimeout(() => {
      updateCanvasElementAPI({
        type: shape.type,
        _id: shape._id,
        transNo: 1,
        floorId: shape.floorId,
        structure: shape.structure,
        size: size,
        position: boundedPosition,
        color: shape.color,
        branchId: branch._id,
        rotation: shape.rotation || 0,
        texture: shape.texture,
        decorType: shape.decorType,
      });
    }, 500);
    setDebouncedUpdate(timeout);
  };

  // ==== option  selectors ====
  const [menuPosition, setMenuPosition] = useState({ x: '0px', y: '0px' });
  const [isVisible, setIsVisible] = useState(false);

  const handleContextMenu = (e,_,position) => {
    if (!selectedElementDetails) return;
    const containerWidth = containerRef.current.clientWidth;
    e.preventDefault();
    setIsVisible(true);

    const clickX = position.x;
    const clickY = position.y;
    const screenW = containerWidth;
    const screenH = containerWidth;
    const menuW = 100; // Width of the context menu
    const menuH = 100; // Height of the context menu

    // Check if there's enough space on the right
    const maxX = screenW - menuW;
    const newY = clickY > screenH - menuH ? screenH - menuH : clickY;
    const newX = clickX > maxX ? maxX : clickX;

    setMenuPosition({ x: `${newX}px`, y: `${newY}px` });
  };

  const handleItemClick = (action) => {
    switch (action) {
      case 'copy':
        // Implement copy action
        break;
      case 'delete':
        // Implement delete action
        break;
      default:
        break;
    }
    setIsVisible(false);
  };

  // Double-click to enable rotation mode
  const handleDoubleClick = (e, shape) => {
    e.stopPropagation();
    if (shape.type !== shapeTypes.SHAPE) {
      setRotatingElementId(shape._id);
    }
  };

  // Click outside to close rotation mode and save
  const handleContainerClick = (e) => {
    if (rotatingElementId && !isRotatingRef.current) {
      const currentShape = shapesRef.current.find((s) => s._id === rotatingElementId);
      if (currentShape && pendingRotations[rotatingElementId]) {
        // Save rotation to backend
        updateCanvasElementAPI({
          type: currentShape.type,
          _id: currentShape._id,
          transNo: 1,
          floorId: currentShape.floorId,
          structure: currentShape.structure,
          size: currentShape.size,
          position: currentShape.position,
          color: currentShape.color,
          branchId: branch._id,
          rotation: currentShape.rotation || 0,
          texture: currentShape.texture,
          decorType: currentShape.decorType,
        });
      }
      setPendingRotations((prev) => {
        const newPending = { ...prev };
        delete newPending[rotatingElementId];
        return newPending;
      });
      setRotatingElementId(null);
    }
  };

  // Get angle from center of element to mouse position
  const getAngle = (centerX, centerY, mouseX, mouseY) => {
    const deltaX = mouseX - centerX;
    const deltaY = mouseY - centerY;
    return Math.atan2(deltaY, deltaX) * (180 / Math.PI);
  };

  // Start rotation drag
  const handleRotationStart = (e, shape) => {
    e.stopPropagation();
    e.preventDefault();
    isRotatingRef.current = true;
    
    // Use the mouse position relative to the shape's center
    // Calculate center based on shape position and size
    const containerRect = containerRef.current.getBoundingClientRect();
    const shapeData = shapesRef.current.find(s => s._id === shape._id);
    if (!shapeData) return;
    
    const centerX = containerRect.left + shapeData.position.x + (shapeData.size.width / 2);
    const centerY = containerRect.top + shapeData.position.y + (shapeData.size.height / 2);
    
    const startAngle = getAngle(centerX, centerY, e.clientX, e.clientY);
    const currentRotation = shapeData.rotation || 0;
    
    rotationStartRef.current = {
      shapeId: shape._id,
      centerX,
      centerY,
      startAngle,
      initialRotation: currentRotation,
    };

    const onMouseMove = (moveEvent) => {
      if (!isRotatingRef.current) return;
      
      const { shapeId, centerX, centerY, startAngle, initialRotation } = rotationStartRef.current;
      const currentAngle = getAngle(centerX, centerY, moveEvent.clientX, moveEvent.clientY);
      const angleDiff = currentAngle - startAngle;
      
      // Snap to 15 degree increments for smoother control
      const snappedRotation = Math.round((initialRotation + angleDiff) / 15) * 15;
      const newRotation = ((snappedRotation % 360) + 360) % 360;
      
      setShapes((prevShapes) =>
        prevShapes.map((s) =>
          s._id === shapeId ? { ...s, rotation: newRotation } : s
        )
      );
      
      setPendingRotations((prev) => ({ ...prev, [shapeId]: true }));
    };

    const onMouseUp = () => {
      isRotatingRef.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

 

  return (
    <div className="tab-panel-container" ref={containerRef} onClick={handleContainerClick}>
      <div className="sub-tab-wrapper"   >
        {shapes?.length > 0 ? (
          shapes?.map((shape) => (
            <Rnd
              enableResizing={shape.type === shapeTypes.CHAIR ? false : true}
              key={shape._id}
              default={{
                x: shape.position.x,
                y: shape.position.y,
                width: shape.size.width,
                height: shape.size.height,
              }}
              minWidth={10}
              minHeight={10}
              bounds="parent"
              onResize={(e, direction, ref, delta, position) =>
                handleSizeChange(
                  shape._id,
                  { width: ref.offsetWidth, height: ref.offsetHeight },
                  position,
                  shape
                )
              }
              onDragStop={(e, d) =>
                handleSizeChange(
                  shape._id,
                  { width: shape.size.width, height: shape.size.height },
                  { x: d.x, y: d.y },
                  shape
                )
              }
            >
              {/* <div onContextMenu={(e) => handleContextMenu(e, shape,{x:shape.position.x,y:shape.position.y})}> */}
              {shape.type === shapeTypes.SHAPE ? (
                <div
                  onClick={() => handleShapeSelected(shape)}
                  className="shape-container "
                  style={{ border: "transparent" }}
                >
                  {shape?.structure === structures.LINESEGMENTTOOL ? (
                    <LineSegmentTool
                      color={
                        selectedElementDetails?._id === shape._id
                          ? "#e91d1d"
                          : shape?.color
                      }
                      width={shape?.size?.width}
                      height={shape?.size?.height}
                    />
                  ) : shape?.structure === structures.SPLITFLOOR ? (
                    <div
                      className="shape-container"
                      style={{
                        backgroundColor:
                          selectedElementDetails?._id === shape._id
                            ? "#e91d1d"
                            : shape?.color,
                      }}
                    />
                  ) : shape?.structure === structures.HORIZONTAL ? (
                    <div
                      className="shape-container"
                      style={{
                        backgroundColor:
                          selectedElementDetails?._id === shape._id
                            ? "#e91d1d"
                            : shape?.color,
                      }}
                    />
                  ) : shape?.structure === structures.STAR ? (
                    <StarTool
                      color={
                        selectedElementDetails?._id === shape._id
                          ? "#e91d1d"
                          : shape?.color
                      }
                      width={shape?.size?.width}
                      height={shape?.size?.height}
                    />
                  ) : (
                    ""
                  )}
                </div>
              ) : shape.type === shapeTypes.TABLE ? (
                <div
                  onClick={() => handleShapeSelected(shape)}
                  onDoubleClick={(e) => handleDoubleClick(e, shape)}
                  className={`shape-container ${shape.structure} ${
                    selectedElementDetails?._id === shape._id ? "selected" : ""
                  }`}
                  style={{
                    background: shape.texture && textureStyles[shape.texture] 
                      ? textureStyles[shape.texture] 
                      : shape.color,
                    transform: `rotate(${shape.rotation || 0}deg)`,
                    boxShadow: shape.texture === 'glass' ? '0 4px 6px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  <span style={{ transform: `rotate(-${shape.rotation || 0}deg)` }}>{shape.name}</span>
                  {/* Rotation Handle - drag to rotate */}
                  {rotatingElementId === shape._id && (
                    <div
                      className="rotation-handle-wrapper"
                      onMouseDown={(e) => handleRotationStart(e, shape)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="rotation-handle">
                        <RotateRightIcon fontSize="small" />
                      </div>
                    </div>
                  )}
                </div>
              ) : shape.type === shapeTypes.DECOR ? (
                <div
                  onClick={() => handleShapeSelected(shape)}
                  onDoubleClick={(e) => handleDoubleClick(e, shape)}
                  className={`decor-container ${
                    selectedElementDetails?._id === shape._id ? "selected" : ""
                  }`}
                  style={{
                    transform: `rotate(${shape.rotation || 0}deg)`,
                    position: 'relative',
                  }}
                >
                  {shape.decorType === decorTypes.PLANT ? (
                    <PlantIcon
                      color={selectedElementDetails?._id === shape._id ? "#e91d1d" : shape?.color || "#4CAF50"}
                      width={shape?.size?.width}
                      height={shape?.size?.height}
                      name={shape?.name}
                    />
                  ) : shape.decorType === decorTypes.TREE ? (
                    <TreeIcon
                      color={selectedElementDetails?._id === shape._id ? "#e91d1d" : shape?.color || "#2E7D32"}
                      width={shape?.size?.width}
                      height={shape?.size?.height}
                      name={shape?.name}
                    />
                  ) : shape.decorType === decorTypes.FLOWER ? (
                    <FlowerIcon
                      color={selectedElementDetails?._id === shape._id ? "#e91d1d" : shape?.color || "#E91E63"}
                      width={shape?.size?.width}
                      height={shape?.size?.height}
                      name={shape?.name}
                    />
                  ) : shape.decorType === decorTypes.FOUNTAIN ? (
                    <FountainIcon
                      color={selectedElementDetails?._id === shape._id ? "#e91d1d" : shape?.color || "#2196F3"}
                      width={shape?.size?.width}
                      height={shape?.size?.height}
                      name={shape?.name}
                    />
                  ) : shape.decorType === decorTypes.STATUE ? (
                    <StatueIcon
                      color={selectedElementDetails?._id === shape._id ? "#e91d1d" : shape?.color || "#9E9E9E"}
                      width={shape?.size?.width}
                      height={shape?.size?.height}
                      name={shape?.name}
                    />
                  ) : shape.decorType === decorTypes.LAMP ? (
                    <LampIcon
                      color={selectedElementDetails?._id === shape._id ? "#e91d1d" : shape?.color || "#FFC107"}
                      width={shape?.size?.width}
                      height={shape?.size?.height}
                      name={shape?.name}
                    />
                  ) : (
                    <PlantIcon
                      color={selectedElementDetails?._id === shape._id ? "#e91d1d" : shape?.color || "#4CAF50"}
                      width={shape?.size?.width}
                      height={shape?.size?.height}
                      name={shape?.name}
                    />
                  )}
                  {/* Rotation Handle - drag to rotate */}
                  {rotatingElementId === shape._id && (
                    <div
                      className="rotation-handle-wrapper"
                      onMouseDown={(e) => handleRotationStart(e, shape)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="rotation-handle">
                        <RotateRightIcon fontSize="small" />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className="chair-container"
                  onClick={() => handleShapeSelected(shape)}
                  onDoubleClick={(e) => handleDoubleClick(e, shape)}
                  style={{
                    transform: `rotate(${shape.rotation || 0}deg)`,
                    position: 'relative',
                  }}
                >
                  <Chair
                    color={
                      selectedElementDetails?._id === shape._id
                        ? "#e91d1d"
                        : shape?.color
                    }
                    width={shape?.size?.width}
                    name={shape?.name}
                    height={shape?.size?.height}
                  />
                  {/* Rotation Handle - drag to rotate */}
                  {rotatingElementId === shape._id && (
                    <div
                      className="rotation-handle-wrapper"
                      onMouseDown={(e) => handleRotationStart(e, shape)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="rotation-handle">
                        <RotateRightIcon fontSize="small" />
                      </div>
                    </div>
                  )}
                </div>
              )}
               {/* </div> */}
            </Rnd>
          ))
        ) : (
          <div className="not-found-container">
            {" "}
            <p>No Floor Plan added .!</p>
          </div>
        )}
          {isVisible && (
        <div
          className="context-menu"
          style={{ top: menuPosition.y, left: menuPosition.x }}
        >
          <div
            className="context-menu-item"
            onClick={() => handleItemClick("copy")}
          >
            Copy
          </div>
          <div
            className="context-menu-item"
            onClick={() => handleItemClick("delete")}
          >
            Delete
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TabPanel;
